import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { ImageAttachment, AskUserQuestionData, CancelPromptPayload } from '@voice-assistant/shared';

// ─── Region types ─────────────────────────────────────────────────────────────

/**
 * State of a single outbound message. Multiple sends may be in-flight in extreme
 * cases (the queue is an array), though the normal case is zero or one.
 *
 * Transitions (implemented in Phase 2; `queued` added by session-inflight-state-integrity
 * Phase 1, the DB-backed mid-turn prompt queue):
 *   idle → submitting  (on send)
 *   submitting → awaiting_stream  (on MESSAGE_RECEIVED ack from server)
 *   submitting → queued  (on PROMPT_QUEUED — the turn ahead is still running; server accepted
 *                          the prompt into the DB queue instead of refusing it)
 *   queued → awaiting_stream  (on MESSAGE_RECEIVED — the drain dispatched this prompt, matched
 *                               by the SAME clientMsgId the queued row carried)
 *   submitting → send_failed{no_ack}  (on 5 s client-side timeout, overridden by reconcile)
 *   submitting → send_failed{reconcile_unmatched}  (on SESSIONS_RECONCILE without clientMsgId)
 *   submitting → awaiting_stream  (on SESSIONS_RECONCILE with matching clientMsgId)
 *   awaiting_stream → streaming  (on first CLAUDE_CHUNK; seeds bufferedText with the chunk)
 *   streaming → streaming  (on each subsequent CLAUDE_CHUNK; appends to bufferedText)
 *   streaming → done  (on CLAUDE_DONE; bufferedText discarded with the variant)
 *   awaiting_stream|streaming → send_failed{stream_stalled}  (on the stream-stall watchdog:
 *                          STREAM_STALL_MS of silence with no terminal frame — CS-1, plan §9.3)
 *   streaming → awaiting_human_answer  (on AGENT_QUESTION — model raised a question)
 *   awaiting_human_answer → streaming  (on human answer submitted — resumed turn streams)
 *   submitting → [removed]  (on FEATURE_GATED — send denied, prune entry)
 *
 * `queued` is deliberately NOT reachable from `onReconcile`/`markSendFailed`/the 5-min stuck
 * sweep — a queued row is server-held (may legitimately wait minutes for the turn ahead to
 * finish) but not yet a saved message, so none of the "did the send actually land" failure
 * detectors apply to it (see their guards: onReconcile only touches `submitting`; the stuck
 * sweep in useChatShell.ts only touches `submitting`).
 *
 * Because nothing server-authoritative can ever resolve a `queued` entry on a LATER page, it is
 * PAGE-SCOPED (voice-assistant#2174, defect 3): it is the wait-indicator of the LIVE page that
 * received PROMPT_QUEUED — the only page that will ever see the drain's MESSAGE_RECEIVED — so it is
 * never persisted (`partialize`) and never hydrated (`merge`): a closed/killed tab cannot leave one
 * behind to read busy on every later page of the shared browser profile. Its only ways out are the
 * drain's ack (`onMessageReceived`) and the operator's Cancel (`commitCancel`). Dropping it on a
 * fresh page loses nothing the server does not still hold: the queue row is server-owned, and busy
 * is re-armed from the server's own view for as long as a turn is really running.
 *   queued → [removed]  (on Cancel — `commitCancel` withdraws it locally AND names it to the server)
 */
export type OutboundState =
  | { mode: 'idle' }
  | {
      mode: 'submitting';
      clientMsgId: string;
      submittedAt: number;
      text: string;
      attachments: ImageAttachment[];
    }
  | {
      mode: 'queued';
      clientMsgId: string;
      text: string;
      attachments: ImageAttachment[];
      queuedAt: number;
    }
  | { mode: 'awaiting_stream'; clientMsgId: string; serverMsgId: string }
  | { mode: 'streaming'; clientMsgId: string; serverMsgId: string; chunkCount: number; bufferedText: string; sequenceBuffer?: Record<number, string>; expectedSeq?: number; lastOrderedEventId?: string }
  | {
      mode: 'awaiting_human_answer';
      clientMsgId: string;
      serverMsgId: string;
      questions: AskUserQuestionData[];
      // Carried across the question so the resumed turn keeps the pre-question text
      // (the resumed turn's chunks append to the same assistant message).
      bufferedText: string;
    }
  | { mode: 'done'; serverMsgId: string }
  | {
      mode: 'send_failed';
      clientMsgId: string;
      reason: 'no_ack' | 'reconcile_unmatched' | 'error' | 'stream_stalled';
      retryable: true;
    };

/**
 * Input region — everything in the compose panel that has not yet been sent.
 * Migrated from three unsynced stores (localStorage + inputDraftRef + tabCache.inputDraft)
 * into a single source of truth. Migration happens in Phase 1.
 */
export type InputState = {
  draft: string;
  attachments: ImageAttachment[];
  composeExpanded: boolean;
  /** Non-null while a voice transcription is buffered but not yet committed to draft. */
  transcribeBuffer: string | null;
};

/**
 * All per-session state owned by the composer store.
 * Keyed by sessionId — the session that ORIGINATED the state, not the currently
 * active tab. A session's state proceeds independently of which tab is displayed.
 */
export type SessionRegion = {
  input: InputState;
  /** Outbound send queue. Normally zero or one entry; multiple during unusual concurrency. */
  outbound: OutboundState[];
  /**
   * Last timestamp the operator was actively on this session tab.
   * Drives the cross-session pending indicator: a reply that arrived before
   * lastSeenAt is considered "seen" and does not pulse.
   */
  lastSeenAt: number | null;
};

/**
 * Global recording slice — enforces at-most-one mic-holder across all sessions.
 * The audio FSM itself lives in useRecorder (leaf hook); this slice only tracks
 * which session currently holds the mic so other sessions can show a conflict hint.
 */
export type GlobalRecordingSlice = {
  sessionId: string | null;
  startedAt: number | null;
};

// ─── Outbound FSM transition table (R-A1) ──────────────────────────────────────
//
// The declarative transition table for the outbound-send FSM (mirrors §6.2 of
// docs/frontend-state-architecture.md). Exported so setpoint evals can assert the
// FSM's shape (R-A1: every FSM store exports `TRANSITIONS`). `[removed]` marks a
// terminal that prunes the entry entirely (markGated). Note: no count-vs-scenario
// SE gates this table today — the composer suite predates that convention — so the
// stream-stall SE asserts only that the NEW rows are present, not a total count.
export const TRANSITIONS: ReadonlyArray<{ from: string; to: string; trigger: string }> = [
  { from: 'idle', to: 'submitting', trigger: 'commitSend' },
  { from: 'submitting', to: 'awaiting_stream', trigger: 'onMessageReceived' },
  { from: 'submitting', to: 'queued', trigger: 'onPromptQueued' },
  { from: 'queued', to: 'awaiting_stream', trigger: 'onMessageReceived' },
  { from: 'submitting', to: 'awaiting_stream', trigger: 'onReconcile' },
  { from: 'submitting', to: 'send_failed', trigger: 'onReconcile' },
  { from: 'submitting', to: 'send_failed', trigger: 'markSendFailed' },
  { from: 'submitting', to: 'send_failed', trigger: 'onPipelineError' },
  { from: 'submitting', to: '[removed]', trigger: 'markGated' },
  { from: 'awaiting_stream', to: 'streaming', trigger: 'onStreamChunk' },
  { from: 'streaming', to: 'streaming', trigger: 'onStreamChunk' },
  { from: 'awaiting_stream', to: 'done', trigger: 'onStreamDone' },
  { from: 'streaming', to: 'done', trigger: 'onStreamDone' },
  { from: 'awaiting_stream', to: 'done', trigger: 'onPipelineError' },
  { from: 'streaming', to: 'done', trigger: 'onPipelineError' },
  { from: 'awaiting_human_answer', to: 'done', trigger: 'onPipelineError' },
  { from: 'streaming', to: 'awaiting_human_answer', trigger: 'onAgentQuestion' },
  { from: 'awaiting_human_answer', to: 'streaming', trigger: 'onHumanAnswerSubmit' },
  // Stream-stall watchdog (CS-1, plan §9.3): a silence-stalled in-flight stream
  // with no terminal frame is force-failed after STREAM_STALL_MS.
  { from: 'awaiting_stream', to: 'send_failed', trigger: 'markStreamStalled' },
  { from: 'streaming', to: 'send_failed', trigger: 'markStreamStalled' },
  // The operator's Cancel withdraws the composer's queued prompts (#2174 defect 3). The named ids
  // also travel to the server (CancelPromptPayload.withdrawClientMsgIds) so the queue ROW dies too.
  { from: 'queued', to: '[removed]', trigger: 'commitCancel' },
  // The server's typed SESSION_IDLE (it freed a slot without a terminal frame — a ghost claim released
  // or expired, a zombie evicted) releases a SERVER-ARMED busy state: only the synthetic `server-*`
  // streaming entry markServerBusy injects, never a local send (#2174 defect 2).
  { from: 'streaming', to: '[removed]', trigger: 'markServerIdle' },
];

// ─── Store interface ──────────────────────────────────────────────────────────

export interface ComposerStore {
  sessions: Map<string, SessionRegion>;
  globalRecording: GlobalRecordingSlice;
  // ── Input-region actions (Phase 1) ──────────────────────────────────────────
  /** Set or clear the draft for a session. Single source of truth — no parallel stores. */
  setDraft: (sessionId: string, draft: string) => void;
  /**
   * Prefill a session's draft from an external producer (quote, slash command, catchup,
   * consult, plan "Run here", review/share/explain flows). `mode: 'append'` concatenates
   * after the existing draft with a blank-line separator (only when the draft is non-empty);
   * `mode: 'replace'` overwrites. Synchronous + explicit sessionId — replaces the old
   * `prefill-input` CustomEvent bus (platform-isolation #1657 Phase 2b). Focus is a separate
   * view concern (the `focus-composer` event), NOT part of this state transition.
   */
  appendOrReplaceDraft: (sessionId: string, text: string, mode: 'replace' | 'append') => void;
  // ── Outbound-region actions (Phase 2) ──────────────────────────────────────
  /**
   * Atomic send: appends a `submitting` outbound entry AND clears the input region
   * (draft + attachments + transcribeBuffer) in one store write.
   * Must be the ONLY mutation for user-initiated sends — never two separate calls.
   */
  commitSend: (sessionId: string, payload: { text: string; attachments: ImageAttachment[]; clientMsgId: string }) => void;
  /** Transitions `submitting → awaiting_stream` OR `queued → awaiting_stream` for the entry
   *  matching clientMsgId (a drained queued prompt acks with its original clientMsgId). */
  onMessageReceived: (sessionId: string, clientMsgId: string, serverMsgId: string) => void;
  /**
   * The operator's Cancel, as ONE intent (five-elements #4 / R-A2): atomically withdraws every `queued`
   * outbound entry of the session and RETURNS the clientMsgIds it withdrew, so the wire can name them to
   * the server (`CancelPromptPayload.withdrawClientMsgIds`) — a local-only clear would leave the queue ROW
   * alive and the "cancelled" prompt would run the moment the aborted turn frees the slot (#2174 defect 3).
   * Touches ONLY `queued` entries: a running stream ends with the server's own terminal (PROMPT_CANCELLED /
   * CLAUDE_DONE — plus useSocket's 5 s onStreamDone fallback), and a `submitting` send ends with its ack or
   * the reconcile. Idempotent and allocation-free when there is nothing queued (Cancel is tapped twice).
   */
  commitCancel: (sessionId: string) => string[];
  /** Transitions `submitting → queued` for the entry matching clientMsgId — the server accepted
   *  this mid-turn prompt into its DB-backed queue instead of refusing it (Phase 1 prompt queue).
   *  A no-op if no matching `submitting` entry exists (e.g. a stale/duplicate ack). */
  onPromptQueued: (sessionId: string, clientMsgId: string) => void;
  /**
   * Reconciles the outbound queue after reconnect.
   * - submitting entries whose clientMsgId IS in receivedClientMsgIds → awaiting_stream
   * - submitting entries whose clientMsgId is NOT in receivedClientMsgIds → send_failed{reconcile_unmatched}
   */
  onReconcile: (sessionId: string, receivedClientMsgIds: string[]) => void;
  /**
   * Transitions `awaiting_stream → streaming` (first chunk) or increments chunkCount.
   * `text` (optional) is the chunk's text, appended to the streaming entry's
   * `bufferedText` accumulator so the machine owns the in-flight streaming text
   * (streambuf-to-outbound migration). Omit `text` for back-compat callers that
   * only signal a chunk arrived; the accumulator is then left unchanged.
   */
  onStreamChunk: (sessionId: string, payload?: string | import('@voice-assistant/shared').StreamChunkEnvelope) => void;
  /** Transitions `streaming → done` (or `awaiting_stream → done` if chunks were missed). */
  onStreamDone: (sessionId: string) => void;
  /**
   * Seeds the in-flight streaming entry's `bufferedText` with `text` (used on
   * mid-stream reconnect, where the server replays a `partialText` snapshot and
   * subsequent CLAUDE_CHUNK events must append to the full partial — not just the
   * new chunks). Seeds the first `streaming`/`awaiting_stream`/`awaiting_human_answer`
   * entry; if none exists (fresh reload), creates a `streaming` entry so the
   * machine owns the reconnected stream. (streambuf-to-outbound migration, Phase 3.)
   */
  seedStreamingBuffer: (sessionId: string, text: string) => void;
  /**
   * Clears all in-flight outbound entries on a pipeline error (crash / WsEvent.ERROR).
   * submitting → send_failed{error}; streaming/awaiting_stream/awaiting_human_answer → done.
   * Replaces onStreamDone in the ERROR handler so submitting entries are not left stuck.
   */
  onPipelineError: (sessionId: string) => void;
  /** Marks a submitting entry as failed (e.g. 5 s no-ack timeout). */
  markSendFailed: (sessionId: string, clientMsgId: string, reason: 'no_ack' | 'reconcile_unmatched' | 'error') => void;
  /**
   * Force-fails a silence-stalled in-flight stream (CS-1, plan §9.3). The matching
   * `awaiting_stream`/`streaming` entry (by clientMsgId) transitions to
   * `send_failed{stream_stalled}` — the terminal the stream-stall watchdog fires when
   * a turn goes STREAM_STALL_MS with no chunk and no terminal frame. Emitting
   * `WsEvent.CANCEL_PROMPT` (server abort) is the wire's job (composer wire); this is
   * only the local FSM transition. No-op if the entry already reached a terminal.
   */
  markStreamStalled: (sessionId: string, clientMsgId: string) => void;
  /**
   * Clears any `send_failed` outbound entries for a session — the dismiss action for
   * the composer's user-visible "turn failed" notice (CS-3). Housekeeping (prunes
   * entries), not an FSM transition — no `TRANSITIONS` row (cf. markGated/markServerIdle).
   */
  dismissSendFailed: (sessionId: string) => void;
  /** Transitions `streaming → awaiting_human_answer` when the model raises an AskUserQuestion. */
  onAgentQuestion: (sessionId: string, serverMsgId: string, questions: AskUserQuestionData[]) => void;
  /**
   * Transitions `awaiting_human_answer → streaming` when the operator submits their answer.
   * The answer travels to the backend out-of-band as a discrete AGENT_QUESTION_ANSWER request
   * (the headless SDK already auto-errored the question and ended the turn — there is no held
   * tool_result call). The resumed turn streams into the SAME outbound entry, so the
   * pre-question bufferedText is preserved.
   */
  onHumanAnswerSubmit: (sessionId: string) => void;
  /** Removes the matching `submitting` entry when FEATURE_GATED denies a send. */
  markGated: (sessionId: string, clientMsgId: string) => void;
  // ── Phase 3 actions ────────────────────────────────────────────────────────
  /** Toggles the compose panel's expanded (bottom-sheet) state for a session. */
  setComposeExpanded: (sessionId: string, expanded: boolean) => void;
  /** Sets or clears the voice transcribe-only buffer (text not yet committed to draft). */
  setTranscribeBuffer: (sessionId: string, text: string | null) => void;
  /** Records that the operator was last active on this session at `timestamp`. */
  updateLastSeenAt: (sessionId: string, timestamp: number) => void;
  /** Updates the global mic-holder slot (at-most-one recording session at a time). */
  setGlobalRecording: (sessionId: string | null, startedAt: number | null) => void;
  // ── Orchestration gate (Phase 4) ───────────────────────────────────────────
  /**
   * Transient set of session IDs where an orchestration operation (/handoff,
   * context-reset) is in progress. Treated as busy by all selectors so the input
   * bar stays locked while CONTEXT_HIGH/CONTEXT_RESETTING → CONTEXT_DOCS_UPDATED
   * events flow. NOT persisted (volatile mid-operation state).
   */
  orchestrationGated: Set<string>;
  /** Adds sessionId to the orchestration gate (CONTEXT_HIGH / CONTEXT_RESETTING). */
  startOrchestration: (sessionId: string) => void;
  /** Removes sessionId from the orchestration gate (CONTEXT_DOCS_UPDATED / resetContext). */
  endOrchestration: (sessionId: string) => void;
  // ── Server-originated busy — non-local producers (#1247) ─────────────────────
  /**
   * Marks a session busy for a SERVER-originated turn that had no local `commitSend`
   * — e.g. a query already in flight when the user (re)connects (QUERY_IN_PROGRESS),
   * an autonomous wave turn, or a remote Continue. Injects an idempotent synthetic
   * `streaming` outbound entry (clientMsgId `server-…`) so `getIsBusy` reads true AND
   * `onStreamChunk` has an entry to append replayed chunks to — WITHOUT the input-bar
   * lock that `orchestrationGated` imposes (the operator may keep typing). No-op if any
   * in-flight entry (submitting/awaiting_stream/streaming) already exists. Cleared by the
   * normal stream terminals (onStreamDone/onPipelineError) or markServerIdle.
   */
  markServerBusy: (sessionId: string) => void;
  /**
   * Clears server-originated busy: removes any `server-`-prefixed outbound entries
   * (local UUID-clientMsgId entries untouched). For producers that end WITHOUT a
   * stream terminal — a cancelled wave, and (#2174 defect 2) the server's typed
   * `SESSION_IDLE`: it freed a slot with no turn behind it (a ghost claim released or
   * expired) or reaped a silent zombie, so no terminal frame will ever clear the busy
   * state a page armed from a server snapshot. The stream terminals already cover the
   * normal finish. No-op (and allocation-free) for a session with no server-armed entry.
   */
  markServerIdle: (sessionId: string) => void;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

export const DEFAULT_INPUT_STATE: InputState = {
  draft: '',
  attachments: [],
  composeExpanded: false,
  transcribeBuffer: null,
};

export const DEFAULT_SESSION_REGION: SessionRegion = {
  input: DEFAULT_INPUT_STATE,
  outbound: [],
  lastSeenAt: null,
};

// ─── Map serializer ───────────────────────────────────────────────────────────
// JSON.stringify/parse do not round-trip Map natively.
// These replacer/reviver functions let the persist middleware serialize the
// sessions Map correctly. Covered by SE-CSM-STORE-SHAPE round-trip assertion.

function mapReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Map) {
    return { __type: 'Map', entries: [...value.entries()] };
  }
  return value;
}

function mapReviver(_key: string, value: unknown): unknown {
  if (
    value !== null &&
    typeof value === 'object' &&
    (value as Record<string, unknown>).__type === 'Map'
  ) {
    return new Map(
      (value as { entries: [string, SessionRegion][] }).entries,
    );
  }
  return value;
}

const mapAwareStorage = createJSONStorage(() => localStorage, {
  replacer: mapReplacer,
  reviver: mapReviver,
});

/**
 * Hydrate-side half of the page-scoped `queued` rule: a copy of `sessions` with every `queued`
 * outbound entry removed. Regions with no queued entry are passed through by reference.
 */
function dropQueuedEntries(sessions: Map<string, SessionRegion>): Map<string, SessionRegion> {
  const out = new Map<string, SessionRegion>();
  for (const [sid, region] of sessions) {
    const outbound = region.outbound.filter((e) => e.mode !== 'queued');
    out.set(sid, outbound.length === region.outbound.length ? region : { ...region, outbound });
  }
  return out;
}

// ─── Store creation ───────────────────────────────────────────────────────────

export const useComposerStore = create<ComposerStore>()(
  persist(
    (set): ComposerStore => ({
      sessions: new Map<string, SessionRegion>(),
      globalRecording: { sessionId: null, startedAt: null },

      setDraft: (sessionId, draft) => {
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId) ?? { ...DEFAULT_SESSION_REGION };
          sessions.set(sessionId, {
            ...existing,
            input: { ...existing.input, draft },
          });
          return { sessions };
        });
      },

      appendOrReplaceDraft: (sessionId, text, mode) => {
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId) ?? { ...DEFAULT_SESSION_REGION };
          const current = existing.input.draft ?? '';
          // Mirrors the former InputBar `prefill-input` listener: append only when there is
          // existing non-whitespace draft, separated by a blank line; otherwise replace.
          const next =
            mode === 'append' && current.trim()
              ? current.trimEnd() + '\n\n' + text
              : text;
          sessions.set(sessionId, {
            ...existing,
            input: { ...existing.input, draft: next },
          });
          return { sessions };
        });
      },

      commitSend: (sessionId, { text, attachments, clientMsgId }) => {
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId) ?? { ...DEFAULT_SESSION_REGION };
          const newEntry: OutboundState = {
            mode: 'submitting',
            clientMsgId,
            submittedAt: Date.now(),
            text,
            attachments,
          };
          sessions.set(sessionId, {
            ...existing,
            input: { ...DEFAULT_INPUT_STATE },
            outbound: [...existing.outbound, newEntry],
          });
          return { sessions };
        });
      },

      onMessageReceived: (sessionId, clientMsgId, serverMsgId) => {
        let transitioned = false;
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId);
          if (!existing) return {};
          const outbound = existing.outbound.map((entry): OutboundState => {
            // Matches BOTH submitting (the ordinary ack path) and queued (a drained prompt acks
            // with the SAME clientMsgId the queued row carried — this is the loop-closure back
            // to the original send; without it a drained prompt hangs in `queued` forever).
            if ((entry.mode === 'submitting' || entry.mode === 'queued') && entry.clientMsgId === clientMsgId) {
              transitioned = true;
              return { mode: 'awaiting_stream', clientMsgId, serverMsgId };
            }
            return entry;
          });
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
        // Arm the stream-stall watchdog for the now-awaiting_stream entry: if no
        // chunk (and no terminal frame) arrives within STREAM_STALL_MS this turn is
        // silence-stalled and gets force-failed (CS-1, plan §9.3).
        if (transitioned) armStreamTimer(sessionId, clientMsgId);
      },

      onPromptQueued: (sessionId, clientMsgId) => {
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId);
          if (!existing) return {};
          const outbound = existing.outbound.map((entry): OutboundState => {
            if (entry.mode === 'submitting' && entry.clientMsgId === clientMsgId) {
              return { mode: 'queued', clientMsgId, text: entry.text, attachments: entry.attachments, queuedAt: Date.now() };
            }
            return entry;
          });
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
      },

      commitCancel: (sessionId) => {
        const withdrawn: string[] = [];
        set((state) => {
          const existing = state.sessions.get(sessionId);
          // Allocation-free no-op (Cancel tapped twice; an idle session has nothing queued): bail BEFORE cloning the Map.
          if (!existing || !existing.outbound.some((e) => e.mode === 'queued')) return {};
          const outbound = existing.outbound.filter((entry) => {
            if (entry.mode !== 'queued') return true;
            withdrawn.push(entry.clientMsgId);
            return false;
          });
          const sessions = new Map(state.sessions);
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
        return withdrawn;
      },

      onReconcile: (sessionId, receivedClientMsgIds) => {
        const armIds: string[] = [];
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId);
          if (!existing) return {};
          const received = new Set(receivedClientMsgIds);
          const outbound = existing.outbound.map((entry): OutboundState => {
            if (entry.mode !== 'submitting') return entry;
            if (received.has(entry.clientMsgId)) {
              armIds.push(entry.clientMsgId);
              return { mode: 'awaiting_stream', clientMsgId: entry.clientMsgId, serverMsgId: entry.clientMsgId };
            }
            return { mode: 'send_failed', clientMsgId: entry.clientMsgId, reason: 'reconcile_unmatched', retryable: true };
          });
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
        // A reconciled submit re-enters awaiting_stream (post-reconnect) — arm the
        // watchdog so a matched-but-silent turn cannot re-strand the composer.
        for (const id of armIds) armStreamTimer(sessionId, id);
      },

      onStreamChunk: (sessionId, payload) => {
        // Captured during the map below (no second iteration — this is the hottest
        // store action). Re-arms the stall watchdog so silence is measured SINCE the
        // last chunk (safe for long generations that stream steadily). armStreamTimer
        // itself skips synthetic server-/reconnect: entries.
        let streamingClientMsgId: string | undefined;
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId);
          if (!existing) return {};

          let chunkText = '';
          let seq: number | undefined;
          let eventId: string | undefined;

          if (typeof payload === 'string') {
            chunkText = payload;
          } else if (payload && typeof payload === 'object') {
            chunkText = payload.text || payload.content || '';
            seq = payload.seq;
            eventId = payload._lastEventId;
          }

          const outbound = existing.outbound.map((entry): OutboundState => {
            if (entry.mode === 'awaiting_stream') {
              streamingClientMsgId = entry.clientMsgId;
              const sequenceBuffer: Record<number, string> = {};
              let expectedSeq = 0;
              let nextBufferedText = chunkText;
              // Redis event id of the last IN-ORDER chunk — the gap-recovery readAfter() anchor.
              let lastOrderedEventId: string | undefined;

              if (seq !== undefined) {
                if (seq > expectedSeq) {
                  sequenceBuffer[seq] = chunkText;
                  nextBufferedText = ''; // wait for seq 0
                } else if (seq === expectedSeq) {
                  expectedSeq++;
                  if (eventId) lastOrderedEventId = eventId;
                }
              } else if (eventId) {
                lastOrderedEventId = eventId; // seq-less chunk applied directly
              }

              return { mode: 'streaming', clientMsgId: entry.clientMsgId, serverMsgId: entry.serverMsgId, chunkCount: 1, bufferedText: nextBufferedText, sequenceBuffer, expectedSeq, lastOrderedEventId };
            }
            if (entry.mode === 'streaming') {
              streamingClientMsgId = entry.clientMsgId;
              let sequenceBuffer = entry.sequenceBuffer || {};
              let expectedSeq = entry.expectedSeq || 0;
              let addedText = '';
              // Carry the prior anchor; advance it only when this chunk applies in-order.
              let lastOrderedEventId = entry.lastOrderedEventId;

              if (seq !== undefined) {
                if (seq > expectedSeq) {
                  sequenceBuffer = { ...sequenceBuffer, [seq]: chunkText };
                } else if (seq === expectedSeq) {
                  addedText = chunkText;
                  expectedSeq++;
                  let tempBuffer = { ...sequenceBuffer };
                  while (tempBuffer[expectedSeq] !== undefined) {
                    addedText += tempBuffer[expectedSeq];
                    delete tempBuffer[expectedSeq];
                    expectedSeq++;
                  }
                  sequenceBuffer = tempBuffer;
                  if (eventId) lastOrderedEventId = eventId; // advanced in-order
                }
                // seq < expectedSeq: already applied (replayed chunk) → idempotent no-op
              } else {
                addedText = chunkText;
                if (eventId) lastOrderedEventId = eventId;
              }

              return { ...entry, chunkCount: entry.chunkCount + 1, bufferedText: entry.bufferedText + addedText, sequenceBuffer, expectedSeq, lastOrderedEventId };
            }
            return entry;
          });
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
        // Re-arm on every chunk: the watchdog measures silence SINCE the last chunk,
        // so a healthy long generation never trips it (only a genuine stall does).
        if (streamingClientMsgId) armStreamTimer(sessionId, streamingClientMsgId);
      },

      onStreamDone: (sessionId) => {
        const clearIds: string[] = [];
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId);
          if (!existing) return {};
          const outbound = existing.outbound.map((entry): OutboundState => {
            if (entry.mode === 'streaming' || entry.mode === 'awaiting_stream') {
              clearIds.push(entry.clientMsgId);
              return { mode: 'done', serverMsgId: entry.serverMsgId };
            }
            return entry;
          });
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
        // Terminal frame landed — disarm the watchdog for any entry that left the
        // in-flight modes (the `done` variant no longer carries clientMsgId, so the
        // id MUST be captured during the map, before the rewrite).
        for (const id of clearIds) clearStreamTimer(id);
      },

      seedStreamingBuffer: (sessionId, text) => {
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId) ?? { ...DEFAULT_SESSION_REGION };
          let seeded = false;
          const outbound = existing.outbound.map((entry): OutboundState => {
            if (seeded) return entry;
            if (entry.mode === 'streaming' || entry.mode === 'awaiting_human_answer') {
              seeded = true;
              return { ...entry, bufferedText: text };
            }
            if (entry.mode === 'awaiting_stream') {
              seeded = true;
              return { mode: 'streaming', clientMsgId: entry.clientMsgId, serverMsgId: entry.serverMsgId, chunkCount: 0, bufferedText: text };
            }
            return entry;
          });
          if (!seeded) {
            // Fresh reload mid-stream: no in-flight entry survived (the prior send
            // happened before the page reloaded). Create one so onStreamChunk has a
            // streaming entry to append to and the session reads as busy until done.
            outbound.push({
              mode: 'streaming',
              clientMsgId: `reconnect:${sessionId}`,
              serverMsgId: `reconnect:${sessionId}`,
              chunkCount: 0,
              bufferedText: text,
            });
          }
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
      },

      onPipelineError: (sessionId) => {
        const clearIds: string[] = [];
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId);
          if (!existing) return {};
          const outbound = existing.outbound.map((entry): OutboundState => {
            if (entry.mode === 'submitting') {
              return { mode: 'send_failed', clientMsgId: entry.clientMsgId, reason: 'error', retryable: true };
            }
            if (
              entry.mode === 'streaming' ||
              entry.mode === 'awaiting_stream' ||
              entry.mode === 'awaiting_human_answer'
            ) {
              clearIds.push(entry.clientMsgId);
              return { mode: 'done', serverMsgId: entry.serverMsgId };
            }
            return entry;
          });
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
        // Pipeline crash/error is a terminal — disarm the watchdog for the entries
        // it drained out of the in-flight modes.
        for (const id of clearIds) clearStreamTimer(id);
      },

      markSendFailed: (sessionId, clientMsgId, reason) => {
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId);
          if (!existing) return {};
          const outbound = existing.outbound.map((entry): OutboundState => {
            if (entry.mode === 'submitting' && entry.clientMsgId === clientMsgId) {
              return { mode: 'send_failed', clientMsgId, reason, retryable: true };
            }
            return entry;
          });
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
      },

      onAgentQuestion: (sessionId, serverMsgId, questions) => {
        const clearIds: string[] = [];
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId);
          if (!existing) return {};
          const outbound = existing.outbound.map((entry): OutboundState => {
            if (entry.mode === 'streaming' && entry.serverMsgId === serverMsgId) {
              clearIds.push(entry.clientMsgId);
              return { mode: 'awaiting_human_answer', clientMsgId: entry.clientMsgId, serverMsgId, questions, bufferedText: entry.bufferedText };
            }
            return entry;
          });
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
        // The turn is now waiting on the operator (not silence-stalled) — disarm the
        // watchdog. onHumanAnswerSubmit re-arms when the resumed turn re-enters streaming.
        for (const id of clearIds) clearStreamTimer(id);
      },

      onHumanAnswerSubmit: (sessionId) => {
        const armIds: string[] = [];
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId);
          if (!existing) return {};
          const outbound = existing.outbound.map((entry): OutboundState => {
            if (entry.mode === 'awaiting_human_answer') {
              // Resume after a human answer: the backend runs a fresh resumed turn
              // whose chunks append to the SAME assistant message, so the accumulator
              // must KEEP the pre-question text. Resetting it would blank the message
              // on the next flush.
              armIds.push(entry.clientMsgId);
              return { mode: 'streaming', clientMsgId: entry.clientMsgId, serverMsgId: entry.serverMsgId, chunkCount: 0, bufferedText: entry.bufferedText };
            }
            return entry;
          });
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
        // The resumed turn re-enters `streaming` — re-arm so a resume that never
        // actually streams (identical wedge to CS-1) cannot re-strand the composer.
        for (const id of armIds) armStreamTimer(sessionId, id);
      },

      markStreamStalled: (sessionId, clientMsgId) => {
        clearStreamTimer(clientMsgId);
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId);
          if (!existing) return {};
          const outbound = existing.outbound.map((entry): OutboundState => {
            if (
              (entry.mode === 'awaiting_stream' || entry.mode === 'streaming') &&
              entry.clientMsgId === clientMsgId
            ) {
              return { mode: 'send_failed', clientMsgId, reason: 'stream_stalled', retryable: true };
            }
            return entry;
          });
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
      },

      dismissSendFailed: (sessionId) => {
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId);
          if (!existing) return {};
          const outbound = existing.outbound.filter((e) => e.mode !== 'send_failed');
          if (outbound.length === existing.outbound.length) return {};
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
      },

      markGated: (sessionId, clientMsgId) => {
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId);
          if (!existing) return {};
          const outbound = existing.outbound.filter(
            (e) => !(e.mode === 'submitting' && e.clientMsgId === clientMsgId),
          );
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
      },

      setComposeExpanded: (sessionId, expanded) => {
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId) ?? { ...DEFAULT_SESSION_REGION };
          sessions.set(sessionId, {
            ...existing,
            input: { ...existing.input, composeExpanded: expanded },
          });
          return { sessions };
        });
      },

      setTranscribeBuffer: (sessionId, text) => {
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId) ?? { ...DEFAULT_SESSION_REGION };
          sessions.set(sessionId, {
            ...existing,
            input: { ...existing.input, transcribeBuffer: text },
          });
          return { sessions };
        });
      },

      updateLastSeenAt: (sessionId, timestamp) => {
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId) ?? { ...DEFAULT_SESSION_REGION };
          sessions.set(sessionId, { ...existing, lastSeenAt: timestamp });
          return { sessions };
        });
      },

      setGlobalRecording: (sessionId, startedAt) => {
        set(() => ({ globalRecording: { sessionId, startedAt } }));
      },

      orchestrationGated: new Set<string>(),

      startOrchestration: (sessionId) => {
        set((state) => {
          const orchestrationGated = new Set(state.orchestrationGated);
          orchestrationGated.add(sessionId);
          return { orchestrationGated };
        });
      },

      endOrchestration: (sessionId) => {
        set((state) => {
          const orchestrationGated = new Set(state.orchestrationGated);
          orchestrationGated.delete(sessionId);
          return { orchestrationGated };
        });
      },

      markServerBusy: (sessionId) => {
        set((state) => {
          // Idempotent + hot-path safe: read existing WITHOUT cloning the sessions Map,
          // and bail before any allocation if the session already reads busy. This runs
          // on every CLAUDE_CHUNK / TOOL_ACTIVITY (non-local producers, #1247), so the
          // no-op path must be allocation-free. Busy = a local send OR a prior server-
          // entry is in flight (submitting/awaiting_stream/streaming).
          const existing = state.sessions.get(sessionId);
          if (
            existing?.outbound.some(
              (e) => e.mode === 'submitting' || e.mode === 'queued' || e.mode === 'awaiting_stream' || e.mode === 'streaming',
            )
          ) {
            return {};
          }
          const base = existing ?? { ...DEFAULT_SESSION_REGION };
          const serverId = `server-${sessionId}`;
          const serverEntry: OutboundState = {
            mode: 'streaming',
            clientMsgId: serverId,
            serverMsgId: serverId,
            chunkCount: 0,
            bufferedText: '',
          };
          const sessions = new Map(state.sessions);
          sessions.set(sessionId, { ...base, outbound: [...base.outbound, serverEntry] });
          return { sessions };
        });
      },

      markServerIdle: (sessionId) => {
        set((state) => {
          const sessions = new Map(state.sessions);
          const existing = sessions.get(sessionId);
          if (!existing) return {};
          const isServerEntry = (e: OutboundState): boolean =>
            ('clientMsgId' in e && typeof e.clientMsgId === 'string' && e.clientMsgId.startsWith('server-')) ||
            ('serverMsgId' in e && typeof e.serverMsgId === 'string' && e.serverMsgId.startsWith('server-'));
          const outbound = existing.outbound.filter((e) => !isServerEntry(e));
          if (outbound.length === existing.outbound.length) return {};
          sessions.set(sessionId, { ...existing, outbound });
          return { sessions };
        });
      },
    }),
    {
      name: 'va-composer-v1',
      storage: mapAwareStorage,
      partialize: (state) => ({
        // Strip `bufferedText` from any in-flight `streaming` entry before persist.
        // A mid-stream entry that survives a reload is stale (the reconcile path
        // overwrites it), and the buffered text can be many KB — persisting it on
        // every chunk frame would thrash localStorage for no benefit.
        // (streambuf-to-outbound migration, Phase 0.)
        //
        // `queued` entries are PAGE-SCOPED and are NOT persisted at all (see OutboundState):
        // nothing server-authoritative can resolve one on a LATER page, so a closed/killed tab
        // that wrote one would leave every later page of the profile busy forever (#2174).
        sessions: new Map(
          [...state.sessions.entries()].map(([sid, region]) => [
            sid,
            {
              ...region,
              outbound: region.outbound
                .filter((e) => e.mode !== 'queued')
                .map((e) =>
                  e.mode === 'streaming' || e.mode === 'awaiting_human_answer'
                    ? { ...e, bufferedText: '' }
                    : e,
                ),
            },
          ]),
        ),
        globalRecording: state.globalRecording,
      }),
      // Hydrate side of the same rule: a snapshot that carries a `queued` entry ANYWAY (written by a
      // build that predates the rule, by a sibling tab, or by a tab that died mid-write) must not block
      // a fresh page — drop it on read. Everything else merges exactly as the default shallow merge would.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<ComposerStore>;
        return {
          ...current,
          ...p,
          sessions: dropQueuedEntries(p.sessions instanceof Map ? p.sessions : current.sessions),
        };
      },
    },
  ),
);

// ─── Stream-stall watchdog (CS-1, plan §9.3) ──────────────────────────────────
//
// A per-entry silence timer that closes the CS-1 hole: `awaiting_stream`/`streaming`
// carry no timestamp, and the 60s stuck-sweep in useChatShell.ts only reaches
// `submitting` — so a rejection/error/stream-drop with NO terminal frame wedged the
// composer on "Working…" forever (✕ Cancel a no-op, message failed silently, reload
// the only recovery). Mirrors sessionLoadStore's armFetchTimer/clearFetchTimer idiom
// (born from the identical #1292 stuck-skeleton bug).
//
// Keyed by clientMsgId (an entry, not a session — the outbound queue is an array).
// Armed on entry into awaiting_stream/streaming, RE-armed on every chunk (so the
// timer measures silence-since-last-chunk — safe for long generations), cleared on
// every terminal (done / pipeline-error / agent-question). On expiry the callback
// re-checks the entry is STILL in-flight (a late terminal already handled it → no-op),
// then emits WsEvent.CANCEL_PROMPT via the composer wire (server abort → frees
// runningQueries so a late chunk can't corrupt a retry — closes CS-2) AND locally
// transitions to send_failed{stream_stalled}. The local transition is NOT gated on the
// cancel ack (mirrors the 5s fallback in useSocket.ts cancelPrompt).
export const STREAM_STALL_MS = 120_000;

// Sliding-deadline implementation. `onStreamChunk` is the app's hottest store action
// (see the "allocation-free" note on markServerBusy just above it), so re-arming must
// NOT churn a clearTimeout+setTimeout per chunk. Instead every chunk bumps a cheap
// `lastActivityAt` number, and a SINGLE timer per entry re-checks silence at its
// boundary: if a chunk landed since it was scheduled it re-schedules for the remaining
// window (never fires on a live stream), else it fires (genuine STREAM_STALL_MS silence).
const streamTimers = new Map<string, ReturnType<typeof setTimeout>>();
const streamLastActivityAt = new Map<string, number>();

/**
 * The wire bridge for the watchdog's server-side abort. Injected once from useSocket.ts
 * (`initComposerWire`) — dependency-injected rather than imported to avoid the
 * store→hook→store cycle (same pattern as sessionLoadStore's SLSWire). Null in the SE
 * simulator, where `cancelPrompt` is simply a no-op.
 */
export interface ComposerWire {
  /** Emit WsEvent.CANCEL_PROMPT for a specific session (per-session, not active-tab). */
  cancelPrompt: (sessionId: string) => void;
}

let _composerWire: ComposerWire | null = null;

export function initComposerWire(wire: ComposerWire): void {
  _composerWire = wire;
}

/**
 * Synthetic outbound entries — reconnect-resume (`reconnect:*`) and server-originated
 * turns (`server-*` from markServerBusy) — are NOT watchdogged here. Their stuck
 * recovery is the server-authoritative SESSIONS_RECONCILE busy-reconcile
 * (useSocket.ts: `getIsBusy && !active → onStreamDone`). Only genuine local sends are.
 */
function isLocalClientMsgId(clientMsgId: string): boolean {
  return !clientMsgId.startsWith('server-') && !clientMsgId.startsWith('reconnect:');
}

function clearStreamTimer(clientMsgId: string): void {
  const t = streamTimers.get(clientMsgId);
  if (t) clearTimeout(t);
  streamTimers.delete(clientMsgId);
  streamLastActivityAt.delete(clientMsgId);
}

/**
 * The watchdog timer body — recursive so it can slide its own deadline. Fires ONLY on
 * genuine silence: at the boundary it re-checks `lastActivityAt`; if a chunk arrived
 * since the timer was scheduled it re-schedules for the remaining window (this is what
 * keeps a live steady stream from ever tripping), else it aborts the turn server-side
 * (WsEvent.CANCEL_PROMPT via the wire) and fails locally (markStreamStalled). The local
 * transition is NOT gated on the cancel ack (mirrors useSocket.ts cancelPrompt's fallback).
 */
function scheduleStreamTimer(sessionId: string, clientMsgId: string, delay: number): void {
  const t = setTimeout(() => {
    const idleFor = Date.now() - (streamLastActivityAt.get(clientMsgId) ?? 0);
    if (idleFor < STREAM_STALL_MS) {
      // A chunk landed since this timer was scheduled — slide the deadline (no per-chunk
      // timer churn: the reschedule happens at most once per STREAM_STALL_MS window).
      scheduleStreamTimer(sessionId, clientMsgId, STREAM_STALL_MS - idleFor);
      return;
    }
    streamTimers.delete(clientMsgId);
    streamLastActivityAt.delete(clientMsgId);
    const state = useComposerStore.getState();
    const region = state.sessions.get(sessionId);
    if (!region) return;
    const entry = region.outbound.find(
      (e) => 'clientMsgId' in e && e.clientMsgId === clientMsgId,
    );
    // Late terminal frame already handled (done / send_failed / awaiting_human_answer)
    // → no-op. Only a still-in-flight stream is a genuine silence-stall.
    if (!entry || (entry.mode !== 'awaiting_stream' && entry.mode !== 'streaming')) return;
    _composerWire?.cancelPrompt(sessionId);
    state.markStreamStalled(sessionId, clientMsgId);
  }, delay);
  streamTimers.set(clientMsgId, t);
}

function armStreamTimer(sessionId: string, clientMsgId: string): void {
  if (!isLocalClientMsgId(clientMsgId)) return;
  // Hot path (called on every chunk): just bump the activity marker — cheap number set,
  // no timer churn. The single timer, if already ticking, will slide its own deadline.
  streamLastActivityAt.set(clientMsgId, Date.now());
  if (streamTimers.has(clientMsgId)) return;
  scheduleStreamTimer(sessionId, clientMsgId, STREAM_STALL_MS);
}

// ─── Cancel wire payload ──────────────────────────────────────────────────────

/**
 * The CANCEL_PROMPT wire payload for ONE Cancel tap — pure and exported so the hook (`useSocket.cancelPrompt`) and
 * the SE simulator build the SAME object (a hand-mirrored copy in the simulator could drift from the hook and go on
 * passing — R-A5). `withdrawnClientMsgIds` is what `commitCancel` just withdrew from the composer: naming them to the
 * server is what makes Cancel mean cancel end-to-end (a local-only clear would leave the queue ROW alive and the
 * "cancelled" prompt would run when the aborted turn frees the slot — #2174 defect 3). Legacy shapes are unchanged:
 * no session → `undefined` (the user-wide abort); nothing withdrawn → `{ sessionId }` (no `withdrawClientMsgIds` key).
 */
export function buildCancelPromptPayload(
  sessionId: string | null | undefined,
  withdrawnClientMsgIds: readonly string[],
): CancelPromptPayload | undefined {
  if (!sessionId) return undefined;
  return withdrawnClientMsgIds.length > 0
    ? { sessionId, withdrawClientMsgIds: [...withdrawnClientMsgIds] }
    : { sessionId };
}

// ─── Selector helpers (read-only, safe to call from any component) ────────────

/** Return the region for a session, or a stable default if not yet created. */
export function getSessionRegion(
  store: ComposerStore,
  sessionId: string,
): SessionRegion {
  return store.sessions.get(sessionId) ?? DEFAULT_SESSION_REGION;
}

/**
 * The in-flight streaming text the machine has accumulated for a session —
 * the `bufferedText` of the head `streaming` (or `awaiting_human_answer`)
 * outbound entry, or `''` if none. Non-hook variant for the chunk-flush
 * scheduler in useSocket.ts (streambuf-to-outbound migration, Phase 2): the
 * flush materialises this into the displayed assistant message.
 */
export function getStreamingBufferedText(
  store: ComposerStore,
  sessionId: string,
): string {
  const region = store.sessions.get(sessionId);
  if (!region) return '';
  for (const e of region.outbound) {
    if (e.mode === 'streaming' || e.mode === 'awaiting_human_answer') {
      return e.bufferedText;
    }
  }
  return '';
}

/**
 * Gap-recovery anchor for a session's in-flight stream: the first missing chunk seq
 * (`expectedSeq`) + the Redis event id of the last IN-ORDER chunk applied (`lastEventId`).
 * Returned only for a `streaming` entry that has a tracked anchor; null otherwise.
 * Consumed by the useSocket CLAUDE_CHUNK handler to emit `WsEvent.REQUEST_MISSING_CHUNKS`
 * on a detected gap — the backend re-reads the Redis stream from `lastEventId` and re-sends
 * CLAUDE_CHUNK events with `seq >= expectedSeq`.
 */
export function getStreamGapRecovery(
  store: ComposerStore,
  sessionId: string,
): { expectedSeq: number; lastEventId?: string } | null {
  const region = store.sessions.get(sessionId);
  if (!region) return null;
  for (const e of region.outbound) {
    if (e.mode === 'streaming') {
      return { expectedSeq: e.expectedSeq ?? 0, lastEventId: e.lastOrderedEventId };
    }
  }
  return null;
}

// ─── Busy selectors (Phase 0 of busy-to-outbound-migration) ───────────────────
//
// These are the ONLY supported way to read per-session "is something happening"
// state. Do NOT derive `busy` from any other source (tabCache.busy, server
// snapshots, etc.) — those are legacy mirrors being phased out.
//
// Busy ≡ at least one outbound entry whose mode is `submitting`, `queued`,
// `awaiting_stream`, or `streaming` OR the session is in `orchestrationGated`
// (CONTEXT_HIGH/CONTEXT_RESETTING in progress). `queued` counts as busy
// (Phase 1 prompt queue) — the underlying turn is genuinely still running, and
// a queued entry may be the ONLY local signal of that (a non-local producer's
// turn with no local `submitting` entry in this browser). `done`, `send_failed`,
// and `awaiting_human_answer` are NOT busy (the turn ended; we await the
// operator's answer, which starts a fresh resumed turn).
//
// 5-min stuck-session safety timeout (Phase 4, implemented in App.tsx):
// ----------------------------------------------------------------------------
// The safety timeout polls every 60s, iterates composerStore.sessions[*].outbound,
// and calls `markSendFailed(sid, clientMsgId, 'no_ack')` on any `submitting`
// entry where `Date.now() - submittedAt > 300_000`. Reason code `'no_ack'`
// preserved (OutboundState union stable). Polling chosen over per-entry setTimeout:
// matches existing semantics, no timer-cleanup on entry transitions.
// ----------------------------------------------------------------------------

/**
 * Predicate over a ComposerStore snapshot — true iff sessionId has at least
 * one outbound entry in submitting/awaiting_stream/streaming. Non-hook variant
 * for callers that already have `useComposerStore.getState()` in hand
 * (e.g. inside effects, event handlers, classifier utilities).
 */
export function getIsBusy(
  store: ComposerStore,
  sessionId: string,
): boolean {
  if (store.orchestrationGated?.has(sessionId)) return true;
  const region = store.sessions.get(sessionId);
  if (!region) return false;
  return region.outbound.some(
    (e) => e.mode === 'submitting' || e.mode === 'queued' || e.mode === 'awaiting_stream' || e.mode === 'streaming',
  );
}

/**
 * React hook — subscribes to busy-state for a single session. Re-renders only
 * when the busy boolean for `sessionId` flips, NOT on every outbound mutation
 * across other sessions. Pass `null` for sessions that don't exist yet — the
 * hook returns false without subscribing to anything destabilising.
 */
export function useIsBusy(sessionId: string | null): boolean {
  return useComposerStore(
    useShallow((state) => {
      if (!sessionId) return false;
      if (state.orchestrationGated?.has(sessionId)) return true;
      return getIsBusy(state, sessionId);
    }),
  );
}

/**
 * React hook — aggregate selector returning the list of session ids that are
 * currently busy (any outbound in submitting/awaiting_stream/streaming). Stable
 * via `useShallow` array equality so SessionListView doesn't thrash on every
 * unrelated store mutation. Use `.length` for the "any busy" aggregate count
 * (drives the brand-signal pill + sort badge in Phase 2).
 */
export function useBusySessionIds(): string[] {
  return useComposerStore(
    useShallow((state) => {
      const ids = new Set<string>();
      state.sessions.forEach((region, sid) => {
        if (
          region.outbound.some(
            (e) => e.mode === 'submitting' || e.mode === 'queued' || e.mode === 'awaiting_stream' || e.mode === 'streaming',
          )
        ) {
          ids.add(sid);
        }
      });
      state.orchestrationGated?.forEach((sid) => ids.add(sid));
      return [...ids];
    }),
  );
}

/**
 * React hook — the `reason` of a `send_failed` outbound entry for a session when the
 * session is otherwise idle, else `null`. Drives the composer's minimal user-visible
 * "turn failed" notice (CS-3): before this, `send_failed` was produced by the store
 * (no_ack / reconcile_unmatched / error / the new stream_stalled) but rendered NOWHERE
 * — the composer silently un-stuck with no signal the turn had failed. Suppressed while
 * a fresh send is in flight (a new attempt supersedes the stale failure).
 */
export function useSendFailed(
  sessionId: string | null,
): 'no_ack' | 'reconcile_unmatched' | 'error' | 'stream_stalled' | null {
  return useComposerStore(
    useShallow((state) => {
      if (!sessionId) return null;
      if (getIsBusy(state, sessionId)) return null;
      const region = state.sessions.get(sessionId);
      if (!region) return null;
      for (const e of region.outbound) {
        if (e.mode === 'send_failed') return e.reason;
      }
      return null;
    }),
  );
}
